import React, { ReactNode, useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { Box } from '@mui/material';

interface RouteKeyWrapperProps {
  children: ReactNode;
}

/**
 * RouteKeyWrapper forces React to unmount and remount the Outlet component
 * when the route changes. This ensures that all child components are properly
 * cleaned up and re-initialized, solving issues with stale state when
 * navigating between routes.
 */
export const RouteKeyWrapper: React.FC<RouteKeyWrapperProps> = ({ children }) => {
  const location = useLocation();
  const previousPathRef = useRef(location.pathname);

  useEffect(() => {
    // Detect route changes
    if (previousPathRef.current !== location.pathname) {
      // Force a small delay to ensure React processes the unmount
      const timer = setTimeout(() => {
        previousPathRef.current = location.pathname;
      }, 0);

      return () => clearTimeout(timer);
    }
  }, [location.pathname]);

  // Generate a unique key based on the pathname
  // This forces React to unmount and remount children when the route changes
  const routeKey = location.pathname;

  return (
    <Box
      key={routeKey}
      sx={{
        width: '100%',
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      {children}
    </Box>
  );
};